import { useState } from 'react';
import { signOut, startSignIn } from '../../auth/TokenProvider';
import { useSession } from '../../auth/useSession';
import { graph } from '../../auth/GraphClient';
import { CategoryManager } from '../components/CategoryManager';
import { TaskManager } from '../components/TaskManager';
import { estimateStorage, formatBytes, isPersisted, requestPersist, type StorageUsage } from '../../lib/storage';
import {
  clearConflictBadge,
  discardOp,
  rebuildLocal,
  resyncFromCloud,
  retryFailed,
  sync,
  useSyncStatus,
} from '../../sync/engine';
import { getSyncDebug } from '../../sync/pull';
import { isAutoEnabled, lastCompletedWeek, setAutoEnabled } from '../../sync/review';
import { listOps } from '../../db/outbox';
import { clearMediaCache, getCacheLimit, setCacheLimit } from '../../sync/mediaFetch';
import type { OutboxOp } from '../../db/schema';
import { countAll } from '../../db/entries';
import { useAsync } from '../hooks/useAsync';
import { getDeviceId } from '../../domain/ids';
import { SCHEMA_VERSION } from '../../domain/types';
import { APP_VERSION } from '../../version';

export function SettingsView() {
  const { session, reload } = useSession();
  const [probe, setProbe] = useState<string | null>(null);
  const [persistTick, setPersistTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const status = useSyncStatus();
  const ops = useAsync<OutboxOp[]>(listOps, [status.pending, status.failed, persistTick], []);
  const [showDebug, setShowDebug] = useState(false);
  const debug = useAsync<Record<string, unknown> | null>(
    () => (showDebug ? getSyncDebug() : Promise.resolve(null)),
    [showDebug, status.lastSyncAt, persistTick],
    null,
  );
  const failedOps = ops.filter((o) => o.status === 'failed');
  const usage = useAsync<StorageUsage | null>(estimateStorage, [persistTick], null);
  const persisted = useAsync<boolean>(isPersisted, [persistTick], false);
  const entries = useAsync(countAll, [], 0);

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <h1>設定</h1>
        </div>
      </header>
      <div className="view-body settings">
        <section>
          <h2>アカウント</h2>
          {session === null ? (
            <p className="muted">確認中…</p>
          ) : session.authenticated ? (
            <>
              <dl className="kv">
                <dt>名前</dt>
                <dd>{session.account?.name || '—'}</dd>
                <dt>メール</dt>
                <dd>{session.account?.email || '—'}</dd>
                {typeof session.secretExpiresInDays === 'number' && (
                  <>
                    <dt>シークレット期限</dt>
                    <dd className={session.secretExpiresInDays < 30 ? 'warn' : undefined}>
                      あと{session.secretExpiresInDays}日
                    </dd>
                  </>
                )}
              </dl>
              <div className="btn-row">
                <button
                  className="ghost-btn"
                  onClick={async () => {
                    setProbe('確認中…');
                    try {
                      const me = await graph.me();
                      const root = await graph.appRoot();
                      setProbe(`OK: ${me.displayName} / フォルダ「${root.name}」（${root.folder?.childCount ?? 0}項目）`);
                    } catch (e) {
                      setProbe(`失敗: ${e instanceof Error ? e.message : String(e)}`);
                    }
                  }}
                >
                  OneDrive接続をテスト
                </button>
                <button
                  className="ghost-btn"
                  onClick={async () => {
                    await signOut();
                    reload();
                  }}
                >
                  サインアウト
                </button>
              </div>
              {probe && <p className="muted probe">{probe}</p>}
            </>
          ) : (
            <>
              <p className="muted">
                OneDriveと同期するにはサインインが必要です。サインインしなくても、この端末に保存された記録の閲覧と作成はできます。
              </p>
              <button className="primary-btn" onClick={() => startSignIn('/settings')}>
                Microsoftアカウントでサインイン
              </button>
            </>
          )}
        </section>

        <section>
          <h2>カテゴリ</h2>
          <CategoryManager />
        </section>

        <section>
          <h2>同期</h2>
          <dl className="kv">
            <dt>状態</dt>
            <dd>
              {status.state === 'syncing' && (status.message ?? '同期中…')}
              {status.state === 'offline' && 'オフライン'}
              {status.state === 'reauth' && <span className="warn">サインインが必要です</span>}
              {status.state === 'error' && <span className="warn">エラー: {status.error}</span>}
              {status.state === 'idle' && (status.pending > 0 ? `未同期 ${status.pending}件` : '同期済み')}
            </dd>
            <dt>最終同期</dt>
            <dd>
              {status.lastSyncAt ? new Date(status.lastSyncAt).toLocaleString('ja-JP') : '—'}
            </dd>
          </dl>
          <div className="btn-row">
            <button
              className="ghost-btn"
              disabled={status.state === 'syncing'}
              onClick={async () => {
                setBusy('sync');
                try {
                  await sync({ manual: true });
                  setProbe('同期しました。');
                } catch (e) {
                  setProbe(`同期に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'sync' ? '同期中…' : '今すぐ同期'}
            </button>
            {status.state === 'reauth' && (
              <button className="primary-btn" onClick={() => startSignIn('/settings')}>
                サインインし直す
              </button>
            )}
          </div>

          {failedOps.length > 0 && (
            <>
              <h3 className="sub-h">同期に失敗した操作（{failedOps.length}件）</h3>
              <ul className="op-list">
                {failedOps.map((o) => (
                  <li key={o.opId}>
                    <div className="op-main">
                      <code>{o.type}</code>
                      <span className="muted">{o.targetKey}</span>
                    </div>
                    <div className="muted op-err">{o.lastError}</div>
                    <button className="icon-btn danger" onClick={() => discardOp(o.opId!)}>
                      破棄
                    </button>
                  </li>
                ))}
              </ul>
              <div className="btn-row">
                <button className="ghost-btn" onClick={() => retryFailed()}>
                  すべて再試行
                </button>
              </div>
            </>
          )}

          {status.conflicts > 0 && (
            <>
              <h3 className="sub-h warn">競合が {status.conflicts} 件あります</h3>
              <p className="muted">
                同じ記録を複数の端末で編集したため、新しい方を採用しました。
                古い方は OneDrive の <code>conflicts/</code> フォルダに保存されています。
              </p>
              <div className="btn-row">
                <button className="ghost-btn" onClick={() => clearConflictBadge()}>
                  確認した
                </button>
              </div>
            </>
          )}

          <div className="btn-row">
            <button
              className="ghost-btn"
              disabled={status.state === 'syncing'}
              onClick={async () => {
                setBusy('resync');
                try {
                  await resyncFromCloud();
                  setProbe('OneDriveから全件取り込み直しました。');
                } catch (e) {
                  setProbe(`失敗しました: ${e instanceof Error ? e.message : String(e)}`);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'resync' ? '取り込み中…' : 'OneDriveから全件取り込み直す'}
            </button>
            <button className="ghost-btn" onClick={() => setShowDebug((v) => !v)}>
              同期の詳細を{showDebug ? '隠す' : '表示'}
            </button>
          </div>

          {showDebug && debug && (
            <pre className="debug-box">{JSON.stringify(debug, null, 2)}</pre>
          )}

          <div className="btn-row">
            <button
              className="ghost-btn"
              onClick={async () => {
                const msg =
                  status.pending > 0
                    ? `未同期の変更が ${status.pending} 件あります。\n端末のデータを破棄してOneDriveから作り直すと、これらは失われます。実行しますか？`
                    : '端末のデータを破棄し、OneDriveから作り直します。よろしいですか？';
                if (!confirm(msg)) return;
                setBusy('rebuild');
                try {
                  await rebuildLocal();
                  setProbe('ローカルデータを作り直しました。');
                } catch (e) {
                  setProbe(`失敗しました: ${e instanceof Error ? e.message : String(e)}`);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'rebuild' ? '作り直し中…' : 'ローカルデータを再構築'}
            </button>
          </div>
        </section>

        <section>
          <h2>タスク</h2>
          <p className="muted">
            デイリータスクは毎日、ウィークリータスクは週の初め（月曜）に入力欄が並びます。
            入力しなかったぶんは保存されません。
          </p>
          <TaskManager />
        </section>

        <section>
          <h2>週のふりかえり（AI）</h2>
          <p className="muted">
            週が終わったあとにアプリを開くと、その週の日記と目標をもとに
            ふりかえりを作ります。生成のときだけ、その週の記録が
            <strong>このサーバー経由で Google Gemini に送信されます</strong>。
            記録は端末とOneDriveの外には保存されません。
          </p>
          <label className="toggle-row">
            <input
              type="checkbox"
              defaultChecked={isAutoEnabled()}
              onChange={(e) => {
                setAutoEnabled(e.target.checked);
                setPersistTick((n) => n + 1);
              }}
            />
            <span>週が終わったら自動で作る</span>
          </label>
          <dl className="kv">
            <dt>直近の対象週</dt>
            <dd>{lastCompletedWeek()}</dd>
          </dl>
          <p className="muted">
            自動生成をオフにしていても、週ビューの「AIに作ってもらう」からいつでも作れます。
            APIキーが未設定の場合はその旨のエラーが表示されます。
          </p>
        </section>

        <section>
          <h2>ストレージ</h2>
          {usage ? (
            <>
              <dl className="kv">
                <dt>使用量</dt>
                <dd>
                  {formatBytes(usage.usage)}
                  {usage.quota > 0 && <span className="muted"> / {formatBytes(usage.quota)}</span>}
                </dd>
                <dt>永続化</dt>
                <dd>
                  {persisted ? (
                    '有効（ブラウザに破棄されにくい状態です）'
                  ) : (
                    <span className="warn">未許可</span>
                  )}
                </dd>
              </dl>
              {!persisted && (
                <div className="btn-row">
                  <button
                    className="ghost-btn"
                    onClick={async () => {
                      await requestPersist();
                      setPersistTick((n) => n + 1);
                    }}
                  >
                    永続化をリクエスト
                  </button>
                </div>
              )}
              {!persisted && (
                <p className="muted">
                  データの正はOneDrive側にあるため失われることはありませんが、
                  未許可のままだとオフラインで開けない場合があります。
                  iPhone・iPadではホーム画面に追加すると安定します。
                </p>
              )}
              <dl className="kv">
                <dt>画像キャッシュ上限</dt>
                <dd>
                  <select
                    value={String(getCacheLimit())}
                    onChange={(e) => {
                      setCacheLimit(Number(e.target.value));
                      setPersistTick((n) => n + 1);
                    }}
                  >
                    <option value={String(200 * 1024 * 1024)}>200 MB</option>
                    <option value={String(500 * 1024 * 1024)}>500 MB</option>
                    <option value={String(2 * 1024 * 1024 * 1024)}>2 GB</option>
                    <option value={String(Number.MAX_SAFE_INTEGER)}>無制限</option>
                  </select>
                </dd>
              </dl>
              <p className="muted">
                直近3か月の画像は上限に関わらず保持します。上限を超えた古い画像は端末から消えますが、
                OneDrive には残り、開いたときに再取得します。
              </p>
              <div className="btn-row">
                <button
                  className="ghost-btn"
                  onClick={async () => {
                    if (!confirm('アップロード済みの画像キャッシュを削除します。よろしいですか？')) return;
                    const freed = await clearMediaCache();
                    setProbe(`${formatBytes(freed)} を解放しました。`);
                    setPersistTick((n) => n + 1);
                  }}
                >
                  画像キャッシュを空にする
                </button>
              </div>
            </>
          ) : (
            <p className="muted">この環境では使用量を取得できません。</p>
          )}
        </section>

        <section>
          <h2>情報</h2>
          <dl className="kv">
            <dt>記録件数</dt>
            <dd>{entries}件</dd>
            <dt>バージョン</dt>
            <dd>{APP_VERSION}</dd>
            <dt>スキーマ</dt>
            <dd>v{SCHEMA_VERSION}</dd>
            <dt>デバイスID</dt>
            <dd><code>{getDeviceId()}</code></dd>
          </dl>
          <div className="btn-row">
            <button
              className="ghost-btn"
              onClick={async () => {
                const reg = await navigator.serviceWorker?.getRegistration();
                if (!reg) {
                  setProbe('この環境ではService Workerが登録されていません（開発サーバーでは無効です）。');
                  return;
                }
                await reg.update();
                setProbe('更新を確認しました。新しいバージョンがあれば通知が表示されます。');
              }}
            >
              アプリの更新を確認
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
